import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { categoryService } from '../services/api';
import './CategoryForm.css';
import { Typography, Row, Col, Card, Input, InputNumber, Select, Switch, Button, Alert, Upload, Space } from 'antd';
const { Title, Text } = Typography;
const { TextArea } = Input;

const CategoryForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentCategory: '',
    isActive: true,
    sortOrder: 0,
    metaTitle: '',
    metaDescription: '',
    specificFields: {}
  });

  const [categories, setCategories] = useState([]);
  const [image, setImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(isEdit);
  const [error, setError] = useState(null);
  const setField = (name, value) => setFormData(prev => ({ ...prev, [name]: value }));

  useEffect(() => {
    loadCategories();
    if (isEdit) {
      loadCategory();
    }
  }, [id]);

  const loadCategories = async () => {
    try {
      const response = await categoryService.getCategories({ includeInactive: false });
      // Exclure la catégorie actuelle si on est en mode édition
      const filteredCategories = isEdit 
        ? response.categories.filter(cat => cat._id !== id)
        : response.categories;
      setCategories(filteredCategories);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
    }
  };

  const loadCategory = async () => {
    try {
      setIsLoadingData(true);
      const response = await categoryService.getCategoryById(id);
      const category = response.category;
      
      setFormData({
        name: category.name || '',
        description: category.description || '',
        parentCategory: category.parentCategory?._id || '',
        isActive: category.isActive ?? true,
        sortOrder: category.sortOrder || 0,
        metaTitle: category.metaTitle || '',
        metaDescription: category.metaDescription || '',
        specificFields: category.specificFields || {}
      });

      // Charger l'image existante
      if (category.image) {
        setImage({
          url: category.image,
          isExisting: true
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement de la catégorie:', error);
      setError('Erreur lors du chargement de la catégorie');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Préparer les données
      const submitData = { ...formData };
      
      // Ajouter l'image si elle existe
      if (image && !image.isExisting) {
        submitData.categoryImage = image.file;
      }

      if (isEdit) {
        await categoryService.updateCategory(id, submitData);
      } else {
        await categoryService.createCategory(submitData);
      }

      navigate('/admin/categories');
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      const backendMessage = err?.response?.data?.message;
      setError(backendMessage || 'Erreur lors de la sauvegarde de la catégorie');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingData) {
    return (
      <div className="category-form-page">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Chargement de la catégorie...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="category-form-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="page-title">
          <Title level={3} style={{ margin: 0 }}>{isEdit ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</Title>
          <Text type="secondary">{isEdit ? 'Modifiez les informations de la catégorie' : 'Créez une nouvelle catégorie'}</Text>
        </div>
        <Space>
          <Button onClick={() => navigate('/admin/categories')}>Retour</Button>
          <Button type="primary" loading={isLoading} onClick={handleSubmit}>{isEdit ? 'Mettre à jour' : 'Créer la catégorie'}</Button>
        </Space>
      </div>

      {error && (
        <Alert type="error" showIcon title="Erreur" description={error} style={{ marginBottom: 16 }} />
      )}

      <form onSubmit={handleSubmit} className="category-form">
        <div className="form-grid">
          {/* Informations générales */}
          <Card className="form-section" title="Informations générales">
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12}>
                <Text strong>Nom de la catégorie *</Text>
                <Input value={formData.name} onChange={(e) => setField('name', e.target.value)} placeholder="Nom de la catégorie" required />
              </Col>
              <Col xs={24} md={12}>
                <Text strong>Catégorie parent</Text>
                <Select
                  value={formData.parentCategory}
                  onChange={(v) => setField('parentCategory', v)}
                  style={{ width: '100%' }}
                  options={[{ value: '', label: 'Aucune (catégorie principale)' }, ...categories.map(c => ({ value: c._id, label: c.name }))]}
                />
              </Col>
              <Col span={24}>
                <Text strong>Description</Text>
                <TextArea rows={4} value={formData.description} onChange={(e) => setField('description', e.target.value)} placeholder="Description de la catégorie" />
              </Col>
              <Col xs={24} md={12}>
                <Text strong>Ordre d'affichage</Text>
                <InputNumber min={0} value={formData.sortOrder} onChange={(v) => setField('sortOrder', v || 0)} style={{ width: '100%' }} />
                <div className="form-help">Les catégories sont triées par ordre croissant (0 = en premier)</div>
              </Col>
              <Col xs={24} md={12}>
                <Text strong>Catégorie active</Text>
                <div><Switch checked={formData.isActive} onChange={(checked) => setField('isActive', checked)} /></div>
              </Col>
            </Row>
          </Card>

          {/* Image */}
          <Card className="form-section" title="Image">
            <div className="form-group">
              <Text strong>Image de la catégorie</Text>
              <Upload beforeUpload={() => false} accept="image/*" maxCount={1} onChange={(info) => {
                const f = info.fileList?.[0]?.originFileObj;
                if (f) setImage({ file: f, isExisting: false });
              }}>
                <Button>Choisir une image</Button>
              </Upload>
              {image && (
                <div className="image-preview" style={{ marginTop: 12 }}>
                  {image.isExisting ? (
                    <img src={`http://localhost:5000${image.url}`} alt="Catégorie" />
                  ) : (
                    <img src={URL.createObjectURL(image.file)} alt="Catégorie" />
                  )}
                  <Button onClick={() => setImage(null)} style={{ marginLeft: 8 }}>Retirer</Button>
                </div>
              )}
            </div>
          </Card>

          {/* Champs spécifiques */}
          <Card className="form-section form-section--full" title="Champs spécifiques">
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">
                Définissez les champs spécifiques qui apparaîtront dans le formulaire de produit pour cette catégorie.
                <br />
                <strong>Exemple :</strong> Pour "ONDULEURS", vous pouvez ajouter "Phase" et "Puissance".
              </Text>
            </div>
            
            <div id="specific-fields-container">
              {Object.entries(formData.specificFields).map(([fieldKey, fieldConfig], index) => (
                <Card key={fieldKey} size="small" style={{ marginBottom: 12, border: '1px solid #f0f0f0' }}>
                  <Row gutter={[12, 12]} align="middle">
                    <Col xs={24} md={6}>
                      <Text strong>Nom du champ</Text>
                      <Input 
                        value={fieldConfig.label || ''} 
                        onChange={(e) => {
                          const newSpecificFields = { ...formData.specificFields };
                          newSpecificFields[fieldKey] = { ...fieldConfig, label: e.target.value };
                          setField('specificFields', newSpecificFields);
                        }}
                        placeholder="Ex: Phase, Puissance"
                      />
                    </Col>
                    <Col xs={24} md={4}>
                      <Text strong>Type</Text>
                      <Select
                        value={fieldConfig.type || 'select'}
                        onChange={(value) => {
                          const newSpecificFields = { ...formData.specificFields };
                          newSpecificFields[fieldKey] = { ...fieldConfig, type: value };
                          setField('specificFields', newSpecificFields);
                        }}
                        style={{ width: '100%' }}
                        options={[
                          { value: 'select', label: 'Liste déroulante' },
                          { value: 'multiselect', label: 'Sélection multiple' }
                        ]}
                      />
                    </Col>
                    <Col xs={24} md={10}>
                      <Text strong>Options (séparées par des virgules)</Text>
                      <Select
                        mode="tags"
                        tokenSeparators={[',']}
                        open={false}
                        value={Array.isArray(fieldConfig.options) ? fieldConfig.options : []}
                        onChange={(values) => {
                          const cleaned = (values || [])
                            .map(v => (typeof v === 'string' ? v.trim() : v))
                            .filter(v => v && String(v).length > 0);
                          const newSpecificFields = { ...formData.specificFields };
                          newSpecificFields[fieldKey] = { ...fieldConfig, options: cleaned };
                          setField('specificFields', newSpecificFields);
                        }}
                        style={{ width: '100%' }}
                        placeholder="Tapez une option puis virgule ou Entrée"
                      />
                    </Col>
                    <Col xs={24} md={4}>
                      <Button 
                        danger 
                        onClick={() => {
                          const newSpecificFields = { ...formData.specificFields };
                          delete newSpecificFields[fieldKey];
                          setField('specificFields', newSpecificFields);
                        }}
                      >
                        Supprimer
                      </Button>
                    </Col>
                  </Row>
                </Card>
              ))}
              
              <Button 
                type="dashed" 
                onClick={() => {
                  const newFieldKey = `field_${Date.now()}`;
                  const newSpecificFields = {
                    ...formData.specificFields,
                    [newFieldKey]: {
                      label: '',
                      type: 'select',
                      options: [],
                      required: false
                    }
                  };
                  setField('specificFields', newSpecificFields);
                }}
                style={{ width: '100%' }}
              >
                + Ajouter un champ spécifique
              </Button>
            </div>
          </Card>

          {/* SEO */}
          <Card className="form-section form-section--full" title="Optimisation SEO">
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12}>
                <Text strong>Meta Title</Text>
                <Input value={formData.metaTitle} onChange={(e) => setField('metaTitle', e.target.value)} maxLength={60} placeholder="Titre pour les moteurs de recherche" />
                <div className="form-help">{formData.metaTitle.length}/60 caractères</div>
              </Col>
              <Col xs={24} md={12}>
                <Text strong>Meta Description</Text>
                <TextArea rows={3} value={formData.metaDescription} onChange={(e) => setField('metaDescription', e.target.value)} maxLength={160} placeholder="Description pour les moteurs de recherche" />
                <div className="form-help">{formData.metaDescription.length}/160 caractères</div>
              </Col>
            </Row>
          </Card>
        </div>

        <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={() => navigate('/admin/categories')}>Annuler</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>{isEdit ? 'Mettre à jour' : 'Créer la catégorie'}</Button>
        </div>
      </form>
    </div>
  );
};

export default CategoryForm;
